/**
 * MenuBar — DS-03 / DS-55.
 *
 * The dropdown strip under the title bar: ملف / عمليات / تقارير / مساعدة, every
 * command carrying the shortcut that fires it. The global keydown listener lives
 * here too, so the shortcut the menu advertises and the shortcut the app answers
 * to are the same table — reception learns F2/F3/F4 in a day and never leaves the
 * keyboard again.
 */

import { forwardRef, useCallback, useEffect, useMemo } from 'react';
import type { HTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Boxes,
  CalendarDays,
  CalendarPlus,
  ClipboardList,
  Info,
  Keyboard,
  LogOut,
  Package,
  PackageMinus,
  PanelLeft,
  Percent,
  ReceiptText,
  Search,
  ShoppingCart,
  TrendingUp,
  UserCheck,
  UserPlus,
} from 'lucide-react';
import { KEYBOARD_SHORTCUTS, PERMISSIONS } from '@nova/shared';
import type { PermissionKey } from '@nova/shared';
import { DropdownMenu, IconButton, type MenuItemSpec } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth.store';
import { useUiStore } from '@/stores/ui.store';
import { useLogout } from './useLogout';

/**
 * The shell owns no screens of its own; these are the paths the feature routers
 * register. The screens that also appear in the sidebar use the exact `to` from
 * `navigation.ts` — two different paths to one screen is two bugs waiting.
 */
const ROUTES = {
  calendar: '/calendar',
  newAppointment: '/appointments/new',
  patients: '/patients',
  newPatient: '/patients/new',
  newInvoice: '/billing/invoices/new',
  consumeStock: '/stock/consume',
  stocktake: '/stock/stocktake',
  newPurchaseInvoice: '/purchasing/new',
  commissionStatements: '/commissions',
  reportMaterialCost: '/reports/material-cost-per-patient',
  reportPnl: '/reports/pnl',
  reportAppointments: '/reports/appointments',
  reportInventory: '/reports/inventory',
} as const;

/**
 * DS-55 — the same table drives the menu labels and the keydown listener, so the
 * two can never drift. Each entry carries the permission that guards the screen:
 * a shortcut that opens a forbidden screen is worse than no shortcut.
 */
const SHORTCUT_TARGETS: Record<string, { path: string; permission: PermissionKey }> = {
  [KEYBOARD_SHORTCUTS.NEW_APPOINTMENT]: {
    path: ROUTES.newAppointment,
    permission: PERMISSIONS.APPOINTMENT_CREATE,
  },
  [KEYBOARD_SHORTCUTS.PATIENT_SEARCH]: {
    path: ROUTES.patients,
    permission: PERMISSIONS.PATIENT_VIEW,
  },
  [KEYBOARD_SHORTCUTS.NEW_PATIENT]: {
    path: ROUTES.newPatient,
    permission: PERMISSIONS.PATIENT_CREATE,
  },
  [KEYBOARD_SHORTCUTS.NEW_INVOICE]: {
    path: ROUTES.newInvoice,
    permission: PERMISSIONS.INVOICE_CREATE,
  },
  [KEYBOARD_SHORTCUTS.TODAY]: {
    path: ROUTES.calendar,
    permission: PERMISSIONS.APPOINTMENT_VIEW,
  },
};

/**
 * SEC-01 — a command the user may not run is removed, not greyed out.
 * `permission` reads like `navigation.ts`: one key means "needs this", an array
 * means "needs at least one of these".
 */
interface CommandSpec extends MenuItemSpec {
  permission?: PermissionKey | PermissionKey[];
}

interface MenuSpec {
  key: string;
  /** Arabic. */
  label: string;
  items: CommandSpec[];
}

/**
 * The keyboard shortcuts are function keys, so they never collide with typing —
 * except inside a field where the browser or the control may already use them,
 * and where the user's hands are clearly busy with data, not navigation.
 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/**
 * Navigating out from under an open dialog throws away whatever was half-typed
 * into it, so a shortcut waits until the dialog is closed.
 */
function hasOpenDialog(): boolean {
  return document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]') !== null;
}

/**
 * The keyboard-shortcut sheet and the about box are shell dialogs that the menu
 * bar does not own. One named handler keeps every command still without a home
 * a single grep away, instead of scattering empty arrows through the tree.
 */
function shellCommandNotWired(): void {}

/** Drops forbidden commands, then repairs a separator left stranded at the top. */
function visibleItems(
  items: CommandSpec[],
  allows: (permission?: PermissionKey | PermissionKey[]) => boolean,
): MenuItemSpec[] {
  const allowed = items
    .filter((item) => allows(item.permission))
    .map(({ permission: _permission, ...item }) => item);

  if (allowed.length && allowed[0].separatorBefore) {
    allowed[0] = { ...allowed[0], separatorBefore: false };
  }
  return allowed;
}

type MenuBarProps = HTMLAttributes<HTMLElement>;

export const MenuBar = forwardRef<HTMLElement, MenuBarProps>(function MenuBar(
  { className, ...rest },
  ref,
) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const can = useAuthStore((state) => state.can);
  const canAny = useAuthStore((state) => state.canAny);
  const user = useAuthStore((state) => state.user);
  // DS-05 — the menu bar owns the rail's show/hide toggle (there is room for it
  // here and the rail spends its width on the tree).
  const sidebarCollapsed = useUiStore((state) => state.sidebarCollapsed);
  const toggleSidebar = useUiStore((state) => state.toggleSidebar);

  const allows = useCallback(
    (permission?: PermissionKey | PermissionKey[]): boolean => {
      // `can`/`canAny` are stable store methods; reading `user` here is what ties
      // the memoised menus to the session, so they reshape on login and logout.
      if (!user) return false;
      if (!permission) return true;
      return Array.isArray(permission) ? canAny(...permission) : can(permission);
    },
    [can, canAny, user],
  );

  // Shared with the mobile navigation drawer, which is the only chrome offering
  // the command once this bar is off screen.
  const logout = useLogout();

  const menus = useMemo<{ key: string; label: string; items: MenuItemSpec[] }[]>(() => {
    const go = (path: string) => () => navigate(path);

    const specs: MenuSpec[] = [
      {
        key: 'file',
        label: t('menu.file'),
        items: [
          {
            key: 'new-patient',
            label: t('menu.newPatient'),
            icon: UserPlus,
            shortcut: KEYBOARD_SHORTCUTS.NEW_PATIENT,
            permission: PERMISSIONS.PATIENT_CREATE,
            onSelect: go(ROUTES.newPatient),
          },
          {
            key: 'new-appointment',
            label: t('menu.newAppointment'),
            icon: CalendarPlus,
            shortcut: KEYBOARD_SHORTCUTS.NEW_APPOINTMENT,
            permission: PERMISSIONS.APPOINTMENT_CREATE,
            onSelect: go(ROUTES.newAppointment),
          },
          {
            key: 'new-invoice',
            label: t('menu.newInvoice'),
            icon: ReceiptText,
            shortcut: KEYBOARD_SHORTCUTS.NEW_INVOICE,
            permission: PERMISSIONS.INVOICE_CREATE,
            onSelect: go(ROUTES.newInvoice),
          },
          {
            key: 'logout',
            label: t('menu.logout'),
            icon: LogOut,
            danger: true,
            separatorBefore: true,
            onSelect: logout,
          },
        ],
      },
      {
        key: 'operations',
        label: t('menu.operations'),
        items: [
          {
            key: 'patient-search',
            label: t('menu.patientSearch'),
            icon: Search,
            shortcut: KEYBOARD_SHORTCUTS.PATIENT_SEARCH,
            permission: PERMISSIONS.PATIENT_VIEW,
            onSelect: go(ROUTES.patients),
          },
          {
            key: 'check-in',
            // APT-06 — check-in is an action on the appointment card, so the
            // command takes reception to today's board rather than to a form.
            label: t('menu.checkIn'),
            icon: UserCheck,
            permission: PERMISSIONS.APPOINTMENT_CHECK_IN,
            onSelect: go(ROUTES.calendar),
          },
          {
            key: 'consume-stock',
            label: t('menu.consumeStock'),
            icon: PackageMinus,
            permission: PERMISSIONS.STOCK_CONSUME,
            onSelect: go(ROUTES.consumeStock),
          },
          {
            key: 'stocktake',
            label: t('menu.stocktake'),
            icon: ClipboardList,
            permission: PERMISSIONS.STOCKTAKE_PERFORM,
            onSelect: go(ROUTES.stocktake),
          },
          {
            key: 'new-purchase-invoice',
            label: t('menu.newPurchaseInvoice'),
            icon: ShoppingCart,
            permission: PERMISSIONS.PURCHASE_CREATE,
            onSelect: go(ROUTES.newPurchaseInvoice),
          },
        ],
      },
      {
        key: 'reports',
        label: t('menu.reports'),
        items: [
          {
            key: 'material-cost',
            label: t('menu.materialCost'),
            icon: Boxes,
            permission: PERMISSIONS.REPORT_INVENTORY,
            onSelect: go(ROUTES.reportMaterialCost),
          },
          {
            key: 'commissions',
            label: t('menu.commissions'),
            icon: Percent,
            // COST-16 — a provider reaches the same screen scoped to their own
            // statement, so their own-view permission opens it too.
            permission: [
              PERMISSIONS.REPORT_COMMISSION,
              PERMISSIONS.COMMISSION_STATEMENT_VIEW_ALL,
              PERMISSIONS.COMMISSION_STATEMENT_VIEW_OWN,
            ],
            onSelect: go(ROUTES.commissionStatements),
          },
          {
            key: 'pnl',
            label: t('menu.pnl'),
            icon: TrendingUp,
            permission: PERMISSIONS.PNL_VIEW,
            onSelect: go(ROUTES.reportPnl),
          },
          {
            key: 'appointments-report',
            label: t('menu.appointmentsReport'),
            icon: CalendarDays,
            permission: PERMISSIONS.REPORT_OPERATIONAL,
            onSelect: go(ROUTES.reportAppointments),
          },
          {
            key: 'inventory-report',
            label: t('menu.inventoryReport'),
            icon: Package,
            permission: PERMISSIONS.REPORT_INVENTORY,
            onSelect: go(ROUTES.reportInventory),
          },
        ],
      },
      {
        key: 'help',
        label: t('menu.help'),
        items: [
          {
            key: 'shortcuts',
            label: t('menu.shortcuts'),
            icon: Keyboard,
            onSelect: shellCommandNotWired,
          },
          {
            key: 'about',
            label: t('menu.about'),
            icon: Info,
            onSelect: shellCommandNotWired,
          },
        ],
      },
    ];

    return specs
      .map((menu) => ({ key: menu.key, label: menu.label, items: visibleItems(menu.items, allows) }))
      // A role with no reports at all should not be shown an empty Reports menu.
      .filter((menu) => menu.items.length > 0);
  }, [allows, logout, navigate, t]);

  // DS-55 — one listener for the whole app, on the same table the menus render.
  useEffect(() => {
    function handleKeydown(event: KeyboardEvent): void {
      if (event.defaultPrevented || event.ctrlKey || event.altKey || event.metaKey) return;
      if (isTypingTarget(event.target) || hasOpenDialog()) return;

      const target = SHORTCUT_TARGETS[event.key];
      if (!target || !can(target.permission)) return;

      // F3 and F6 are browser commands (find, address bar) — take them.
      event.preventDefault();
      navigate(target.path);
    }

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [can, navigate]);

  return (
    <nav
      ref={ref}
      aria-label={t('a11y.menuBar')}
      // DS-03 — slate-700, 32px, sitting directly under the title bar.
      //
      // NFR-05 — hidden below 1024px, not unmounted. This component owns the
      // application's single keydown listener (DS-55), and a phone that has a
      // keyboard attached should still answer F2/F3/F4. Every command it holds
      // is reachable from the navigation drawer or from the screen it belongs
      // to; the one that was not — logout — moved into `useLogout` so the
      // drawer can offer it too.
      className={cn('flex h-menubar shrink-0 items-stretch bg-slate-700 max-lg:hidden', className)}
      {...rest}
    >
      {menus.map((menu) => (
        <DropdownMenu
          key={menu.key}
          align="start"
          sideOffset={0}
          items={menu.items}
          trigger={
            <button
              type="button"
              data-testid={`menubar-${menu.key}`}
              className="px-3 text-xs font-bold text-slate-200 transition hover:bg-slate-600 hover:text-white
                         data-[state=open]:bg-slate-600 data-[state=open]:text-white"
            >
              {menu.label}
            </button>
          }
        />
      ))}

      {/* DS-05 — the rail's toggle rides the far edge of the menu bar, which is
          the one piece of chrome a collapsed rail leaves behind. The icon is
          mirrored in RTL so it always points at the edge the rail sits on. */}
      <div className="ms-auto flex items-center pe-2">
        <IconButton
          icon={PanelLeft}
          flipIcon
          title={sidebarCollapsed ? t('a11y.showSidebar') : t('a11y.hideSidebar')}
          aria-expanded={!sidebarCollapsed}
          onClick={toggleSidebar}
          variant="ghost"
          className="text-slate-200 hover:bg-slate-600 hover:text-white"
        />
      </div>
    </nav>
  );
});
